import * as React from "react";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import type { OAuthAuthorizationDetails } from "@supabase/supabase-js";
import {
  AlertCircle,
  ArrowRight,
  BookOpen,
  Check,
  Dumbbell,
  Mail,
  ShieldCheck,
  User,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase/supabaseClient";

type ConsentDecision = "approve" | "deny" | null;

interface ScopeInfo {
  icon: React.ReactNode;
  label: string;
  description: string;
  required?: boolean;
}

const SCOPE_MAP: Record<string, ScopeInfo> = {
  openid: {
    icon: <ShieldCheck className="h-4 w-4 text-muted-foreground" />,
    label: "Sign you in",
    description: "Verify your identity",
    required: true,
  },
  profile: {
    icon: <User className="h-4 w-4 text-muted-foreground" />,
    label: "View your profile",
    description: "Name, avatar, and account info",
    required: true,
  },
  email: {
    icon: <Mail className="h-4 w-4 text-muted-foreground" />,
    label: "Access your email address",
    description: "Read your verified email",
    required: true,
  },
  "read:workouts": {
    icon: <Dumbbell className="h-4 w-4 text-muted-foreground" />,
    label: "Read your workouts",
    description: "View your training history and plans",
  },
  "write:workouts": {
    icon: <Dumbbell className="h-4 w-4 text-muted-foreground" />,
    label: "Manage your workouts",
    description: "Create and update workouts on your behalf",
  },
  "read:nutrition": {
    icon: <BookOpen className="h-4 w-4 text-muted-foreground" />,
    label: "Read your nutrition data",
    description: "View your meal logs and nutrition plans",
  },
};

function getScopeInfo(scope: string): ScopeInfo {
  return (
    SCOPE_MAP[scope] ?? {
      icon: <ShieldCheck className="h-4 w-4 text-muted-foreground" />,
      label: scope,
      description: "Access requested by this application",
    }
  );
}

function getClientDomain(uri?: string): string {
  if (!uri) return "";
  try {
    return new URL(uri).hostname;
  } catch {
    return uri;
  }
}

export const Route = createFileRoute("/oauth/consent")({
  validateSearch: (search: Record<string, unknown>) => ({
    authorization_id:
      typeof search.authorization_id === "string" ? search.authorization_id : "",
  }),
  beforeLoad: async ({ context, location }) => {
    if (!context.session) {
      const nextPath = location.href;
      throw redirect({
        href: `/login?next=${encodeURIComponent(nextPath)}`,
        replace: true,
      });
    }
  },
  component: OAuthConsentPage,
});

function OAuthConsentPage() {
  const { authorization_id } = Route.useSearch();
  const [details, setDetails] = React.useState<OAuthAuthorizationDetails | null>(
    null,
  );
  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [decision, setDecision] = React.useState<ConsentDecision>(null);

  React.useEffect(() => {
    let ignore = false;

    const loadAuthorizationDetails = async () => {
      if (!authorization_id) {
        setErrorMessage("Authorization request missing or invalid.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      const { data, error } =
        await supabase.auth.oauth.getAuthorizationDetails(authorization_id);

      if (ignore) return;

      if (error) {
        setErrorMessage(error.message);
        setLoading(false);
        return;
      }

      if ("redirect_url" in data) {
        window.location.assign(data.redirect_url);
        return;
      }

      console.log("OAuth authorization details:", data);
      setDetails(data);
      setLoading(false);
    };

    void loadAuthorizationDetails();

    return () => {
      ignore = true;
    };
  }, [authorization_id]);

  const handleDecision = async (action: Exclude<ConsentDecision, null>) => {
    if (!details) return;

    setDecision(action);
    setErrorMessage(null);

    const response =
      action === "approve"
        ? await supabase.auth.oauth.approveAuthorization(
            details.authorization_id,
            { skipBrowserRedirect: true },
          )
        : await supabase.auth.oauth.denyAuthorization(details.authorization_id, {
            skipBrowserRedirect: true,
          });

    if (response.error) {
      setDecision(null);
      setErrorMessage(response.error.message);
      toast.error(
        action === "approve"
          ? "Failed to approve authorization"
          : "Failed to deny authorization",
        { description: response.error.message },
      );
      return;
    }

    window.location.assign(response.data.redirect_url);
  };

  const scopes = details?.scope.split(" ").filter(Boolean) ?? [];
  const clientDomain = getClientDomain(details?.client.uri);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-background p-6 md:p-10">
      <div className="flex w-full max-w-md flex-col gap-8">

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <LoadingSpinner className="h-5 w-5" />
            <p className="text-sm text-muted-foreground">
              Loading authorization details...
            </p>
          </div>
        ) : null}

        {!loading && errorMessage ? (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to load authorization request</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
            <div className="flex justify-center">
              <Button asChild variant="outline">
                <Link to="/">Back to app</Link>
              </Button>
            </div>
          </div>
        ) : null}

        {!loading && !errorMessage && details ? (
          <>
            {/* App connection header */}
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex items-center gap-3">
                {/* Requesting app avatar */}
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border bg-muted text-xl font-semibold">
                  {details.client.logo_uri ? (
                    <img
                      src={details.client.logo_uri}
                      alt={details.client.name ?? "App"}
                      className="h-full w-full rounded-2xl object-cover"
                    />
                  ) : details.client.name ? (
                    details.client.name.charAt(0).toUpperCase()
                  ) : (
                    <ShieldCheck className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>

                <ArrowRight className="h-4 w-4 text-muted-foreground" />

                {/* Trainrr avatar */}
                <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border">
                  <img src="/icon-512.png" alt="Trainrr" className="h-full w-full object-cover" />
                </div>
              </div>

              <div className="space-y-1">
                <h1 className="text-xl font-semibold">
                  {details.client.name ?? "An app"} wants to access your Trainrr account
                </h1>
                {clientDomain ? (
                  <p className="text-sm text-muted-foreground">
                    {clientDomain} is requesting the following permissions
                  </p>
                ) : null}
              </div>
            </div>

            {/* Permissions list */}
            <div className="space-y-1">
              <p className="mb-3 text-sm text-muted-foreground">
                Requested permissions
              </p>

              {scopes.length > 0 ? (
                <div className="divide-y rounded-xl border bg-background">
                  {scopes.map((scope) => {
                    const info = getScopeInfo(scope);
                    return (
                      <div
                        key={scope}
                        className="flex items-center gap-3 px-4 py-3.5"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                          {info.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {info.label}
                            </span>
                            {info.required ? (
                              <Badge
                                variant="secondary"
                                className="rounded-full px-2 py-0 text-xs font-normal"
                              >
                                Required
                              </Badge>
                            ) : null}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {info.description}
                          </p>
                        </div>
                        <Check className="h-4 w-4 shrink-0 text-green-500" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No scopes were provided with this request.
                </p>
              )}
            </div>

            {/* Security notice */}
            <div className="flex items-center gap-3 rounded-xl bg-muted px-4 py-3">
              <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                This app will not have access to your password. You can revoke
                access at any time.
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                variant="ghost"
                className="flex-1 text-base"
                onClick={() => void handleDecision("deny")}
                disabled={decision !== null}
              >
                {decision === "deny" ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    Deny
                  </>
                )}
              </Button>

              <Button
                className="flex-1 text-base"
                onClick={() => void handleDecision("approve")}
                disabled={decision !== null}
              >
                {decision === "approve" ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  "Authorize"
                )}
              </Button>
            </div>

            {/* Signed in as */}
            <p className="text-center text-sm text-muted-foreground">
              Signed in as {details.user.email}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
